import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toBase64 } from '../utils/utils';
import { uploadImagesToS3 } from '../services/s3/s3Service';
import { dictionary } from '../utils/dictionary';
import toast from 'react-hot-toast';
import { FOLDER_IMAGES, MAX_FILE_SIZE } from '../components/onboarding/const/products';
import { sanitizeUploadFilename } from '../utils/sanitizeUploadFilename';

interface UseGenericImageUploadProps {
  maxImages?: number;
  maxFileSize?: number;
  folder: string;
  currentImages: string[];
  onImagesUpdate: (images: string[]) => void;
  allowMultiple?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: (uploadedUrls: string[]) => void;
  onUploadError?: (error: Error) => void;
}

interface UseGenericImageUploadReturn {
  isLoading: boolean;
  error: string;
  handleImageUpload: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  setError: (error: string) => void;
  uploadProgress: {
    current: number;
    total: number;
    isUploading: boolean;
  };
}

export const useGenericImageUpload = ({
  maxImages = Infinity,
  maxFileSize = MAX_FILE_SIZE,
  folder = FOLDER_IMAGES,
  currentImages,
  onImagesUpdate,
  allowMultiple = true,
  onUploadStart,
  onUploadComplete,
  onUploadError
}: UseGenericImageUploadProps): UseGenericImageUploadReturn => {
  const { language } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    isUploading: false
  });

  const handleImageUpload = (e?: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target.files) {
      processFiles(Array.from(e.target.files));
      return;
    }

    const currentImageCount = currentImages.length;
    const remainingSlots = maxImages - currentImageCount;
    
    if (remainingSlots <= 0) {
      setError(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }
  
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png, image/jpg, image/jpeg, image/webp';
    fileInput.multiple = allowMultiple;
  
    fileInput.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;
      processFiles(Array.from(files));
    };
  
    fileInput.click();
  };

  const processFiles = async (files: File[]) => {
    const currentImageCount = currentImages.length;
    const remainingSlots = maxImages - currentImageCount;
    
    if (remainingSlots <= 0) {
      setError(dictionary?.["max-images-reached"]?.[language] || `Ya alcanzaste el límite de ${maxImages} imágenes`);
      return;
    }

    const filesToUpload = allowMultiple 
      ? Array.from(files).slice(0, remainingSlots)
      : [files[0]].filter(Boolean);
    
    const validFiles = filesToUpload.filter(file => file.size <= maxFileSize);

    if (validFiles.length < filesToUpload.length) {
      toast.error(dictionary["max-file-size-per-image"]?.[language] || "El límite de tamaño por imagen es de 5MB");
    }

    if (validFiles.length === 0) {
      setError(dictionary?.["no-valid-files-to-upload"]?.[language] || "No hay archivos válidos para subir");
      return;
    }

    setIsLoading(true);
    setError('');
    onUploadStart?.();
    
    setUploadProgress({
      current: 0,
      total: validFiles.length,
      isUploading: true
    });
    
    try {
      const base64Promises = validFiles.map(file => toBase64(file));
      const base64Results = await Promise.allSettled(base64Promises);
      
      const validBase64Files: string[] = [];
      const validFilesForUpload: File[] = [];
      
      base64Results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          validBase64Files.push(result.value);
          validFilesForUpload.push(validFiles[index]);
        } else {
          console.error(`Error al convertir archivo ${validFiles[index].name}:`, result.reason);
        }
      });

      if (validFilesForUpload.length === 0) {
        throw new Error('Error al procesar las imágenes');
      }

      const baseTimestamp = Date.now();
      const uploadPromises = validFilesForUpload.map(async (file, index) => {
        try {
          const filename = `${baseTimestamp}-${index}-${sanitizeUploadFilename(file.name)}`;
          const response = await uploadImagesToS3({
            file: validBase64Files[index],
            folder: folder,
            filename: filename
          });
          return { success: true, url: response.s3_url };
        } catch (error) {
          console.error(`Error al subir imagen ${file.name}:`, error);
          return { success: false, url: null };
        }
      });

      const uploadResults = await Promise.allSettled(uploadPromises);
      
      const uploadedUrls: string[] = [];
      let failedCount = 0;

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success && result.value.url) {
          uploadedUrls.push(result.value.url);
        } else {
          failedCount++;
          const fileName = validFilesForUpload[index]?.name || 'imagen';
          console.error(`Error al subir ${fileName}:`, result.status === 'fulfilled' ? result.value : result.reason);
        }
      });

      if (uploadedUrls.length > 0) {
        const updatedImages = [...currentImages, ...uploadedUrls];
        onImagesUpdate(updatedImages);
        onUploadComplete?.(uploadedUrls);

        if (failedCount > 0) {
          const baseMessage = dictionary?.["success-uploading-images"]?.[language] || "imagen(es) subida(s) exitosamente. Fallaron {failedCount} imagen(es).";
          const message = baseMessage.replace('{failedCount}', failedCount.toString());
          toast.success(`${uploadedUrls.length} ${message}`);
        } else {
          const baseMessage = dictionary?.["success-uploading-images"]?.[language] || "imagen(es) subida(s) exitosamente";
          toast.success(`${uploadedUrls.length} ${baseMessage}`);
        }
      } else {
        const errorMsg = dictionary?.["error-uploading-images-too-long"]?.[language] || "Error al subir imagenes, el tamaño maximo es de 5MB";
        toast.error(errorMsg);
        const error = new Error(errorMsg);
        onUploadError?.(error);
      }
    } catch (error) {
      console.error('Error al procesar las imágenes:', error);
      const errorMsg = dictionary?.["error-processing-images"]?.[language] || "Error al procesar las imágenes";
      toast.error(errorMsg);
      const err = error instanceof Error ? error : new Error(errorMsg);
      setError(errorMsg);
      onUploadError?.(err);
    } finally {
      setIsLoading(false);
      setUploadProgress({
        current: 0,
        total: 0,
        isUploading: false
      });
    }
  };

  const removeImage = (index: number) => {
    const newImages = currentImages.filter((_, i) => i !== index);
    onImagesUpdate(newImages);
  };

  return {
    isLoading,
    error,
    handleImageUpload,
    removeImage,
    setError,
    uploadProgress
  };
};

